import PortalDialog, {PortalDialogProperties, PortalDialogState} from "../PortalDialog";
import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import PortalDialogType from "../../PortalDialogType";
import MimeTypes from "../../../document/mimetype/MimeTypes";
import {WebServiceType, WebServiceTypes} from "@softvision/webpdf-wsclient-9-typescript";
import MimeTypeFilter from "../../../document/mimetype/MimeTypeFilter";
import Parameter from "../../parameter/Parameter";
import PortalDocument from "../../../document/PortalDocument";
import {Button} from "primereact/button";
import PortalDocumentManager from "../../../document/PortalDocumentManager";
import DialogDefault from "../../parameter/PortalDialogDefault";
import {InputText} from "primereact/inputtext";
import {InputTextarea} from "primereact/inputtextarea";
import {ColorPicker} from "primereact/colorpicker";
import {InputNumber} from "primereact/inputnumber";
import AnnotationSquareFunctionFields from "../../field/annotation/AnnotationSquareFunctionFields";
import validator from "validator";
import MimeType from "../../../document/mimetype/MimeType";
import Message from "../../../../shared/log/Message";
import {MessageType} from "../../../../shared/log/MessageInterface";
import {
	AdminWebserviceStatus,
	BaseToolbox,
	Border,
	BorderStyle,
	DrawMode,
	Functions,
	Information,
	Security,
	Settings,
	SettingsInterface,
	SignatureEditProfile,
	SquareAnnotation,
	ToolboxAnnotationAnnotation
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import PortalDialogSecurity, {Intention} from "../../PortalDialogSecurity";
import Translate from "../../../../shared/translation/Translate";
import Helper from "../../../../shared/components/Helper";
import LengthUnit from "../../../../shared/units/LengthUnit";
import {Dropdown} from "primereact/dropdown";
import Icon from "../../../../shared/components/Icon";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";

export class AnnotationSquareEditDialogDefinition extends PortalDialog<ToolboxAnnotationAnnotation,
	PortalDialogProperties<ToolboxAnnotationAnnotation>, PortalDialogState<ToolboxAnnotationAnnotation>> {
	constructor(props: any) {
		super(props);

		this.state = {
			...this.state,
			fieldErrors: new Map<AnnotationSquareFunctionFields, string>()
		};
	}

	public static isEnabled(portalInfo: Information): boolean {
		return FunctionHelper.applyValue(
			portalInfo.webserviceStatus?.webservices?.toolbox?.enabled, AdminWebserviceStatus.getEnabledDefault()
		) && FunctionHelper.applyValue(
			portalInfo.functionsStatus?.editAnnotation, Functions.getEditAnnotationDefault()
		);
	}

	public static isActive(
		selectedDocuments: Array<PortalDocument>, portalInfo: Information, signatureEditProfile: SignatureEditProfile
	): boolean {
		let mimeTypes: Array<MimeType> = [];

		for (let selectedDocument of selectedDocuments) {
			mimeTypes.push(selectedDocument.getMimeType());

			if (selectedDocument.isLocked()) {
				return false;
			}

			if (!PortalDialogSecurity.checkRestrictions(
				PortalDialogType.ANNOTATION_SQUARE_EDIT, signatureEditProfile, Intention.editingAnnotations,
				selectedDocument
			)) {
				return false;
			}

			if (!selectedDocument.isEditable()) {
				return false;
			}
		}

		return this.isEnabled(portalInfo) && selectedDocuments.length === 1 &&
			MimeTypeFilter.checkSelection(mimeTypes, [MimeTypes.PDF]);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["annotation-square-edit-dialog"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
					header={this.renderHeader()} footer={this.renderFooter()}
					appendTo={document.getElementById("root")} resizable={false}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.annotation_square.edit"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					{this.renderContents()}
				</div>
			</Dialog>
		);
	}

	public renderContents(): ReactNode {
		let parameter: ToolboxAnnotationAnnotation | undefined = this.state.parameter?.data;
		let errors: Map<AnnotationSquareFunctionFields, string> = this.state.fieldErrors;

		if (!FunctionHelper.exists(parameter?.edit?.square)) {
			return null;
		}

		let annotationParameter: SquareAnnotation = parameter!.edit!.square!;

		return (
			<>
				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(AnnotationSquareFunctionFields.CREATOR)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-creator">
							<Translate {...this.props} data="annotation.properties.creator.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8 p-grid">
							<InputText id="control-creator"
									   className={errors.has(AnnotationSquareFunctionFields.CREATOR) ? "p-invalid" : ""}
									   tooltip={errors.has(AnnotationSquareFunctionFields.CREATOR) ? Translate.value(
										   errors.get(AnnotationSquareFunctionFields.CREATOR)!, {ns: "message"}
									   ) : ""}
									   value={FunctionHelper.applyValue(
										   annotationParameter.creator, SquareAnnotation.getCreatorDefault()
									   )}
									   onInput={(e) => this.onChange(
										   AnnotationSquareFunctionFields.CREATOR, e.currentTarget.value
									   )}/>
							{this.renderFieldError(errors.has(AnnotationSquareFunctionFields.CREATOR) ?
								Translate.value(
									errors.get(AnnotationSquareFunctionFields.CREATOR)!, {ns: "message"}
								) : ""
							)}
						</div>
					</div>
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(AnnotationSquareFunctionFields.SUBJECT)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-subject">
							<Translate {...this.props} data="annotation.properties.subject.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8 p-grid">
							<InputText id="control-subject"
									   value={FunctionHelper.applyValue(
										   annotationParameter.subject, SquareAnnotation.getSubjectDefault()
									   )}
									   onInput={(e) => this.onChange(
										   AnnotationSquareFunctionFields.SUBJECT, e.currentTarget.value
									   )}/>
						</div>
					</div>
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(AnnotationSquareFunctionFields.CONTENTS)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-contents">
							<Translate {...this.props} data="annotation.properties.content.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8 p-grid">
							<InputTextarea rows={3} id="control-contents"
										   value={FunctionHelper.applyValue(
											   annotationParameter.contents, SquareAnnotation.getContentsDefault()
										   )}
										   onInput={(e) => this.onChange(
											   AnnotationSquareFunctionFields.CONTENTS, e.currentTarget.value
										   )}/>
						</div>
					</div>
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(AnnotationSquareFunctionFields.INTENTS)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-intents">
							<Translate {...this.props} data="annotation.properties.intents" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8 p-grid">
							<InputText id="control-intents"
									   value={FunctionHelper.applyValue(
										   annotationParameter.intents, SquareAnnotation.getIntentsDefault()
									   )}
									   onInput={(e) => this.onChange(
										   AnnotationSquareFunctionFields.INTENTS, e.currentTarget.value
									   )}/>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(AnnotationSquareFunctionFields.DRAW_MODE)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-draw-mode">
							<Translate {...this.props} data="annotation.appearance.draw_mode.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8 p-grid">
							<Dropdown inputId="control-draw-mode"
									  value={FunctionHelper.applyValue(
										  annotationParameter.drawMode, SquareAnnotation.getDrawModeDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "annotation.appearance.draw_mode.fill_stroke",
												  {ns: "dialog"}
											  ),
											  value: DrawMode.FillAndStroke
										  },
										  {
											  label: Translate.value(
												  "annotation.appearance.draw_mode.fill", {ns: "dialog"}
											  ),
											  value: DrawMode.Fill
										  },
										  {
											  label: Translate.value(
												  "annotation.appearance.draw_mode.stroke", {ns: "dialog"}
											  ),
											  value: DrawMode.Stroke
										  }
									  ]}
									  onChange={(e) => this.onChange(AnnotationSquareFunctionFields.DRAW_MODE, e.value)}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
					<div className="p-col-12 p-grid"
						 hidden={
							 !this.state.fields?.contains(AnnotationSquareFunctionFields.FILL) ||
							 annotationParameter.drawMode === DrawMode.Stroke
						 }>
						<label className="p-col-12 p-sm-4" htmlFor="control-fill">
							<Translate {...this.props} data="annotation.appearance.color.fill" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8">
							<div className={"p-inputgroup" + (
								errors.has(AnnotationSquareFunctionFields.FILL) ? " p-invalid" : ""
							)}>
								<InputText
									className={errors.has(AnnotationSquareFunctionFields.FILL) ? "p-invalid" : ""}
									tooltip={errors.has(AnnotationSquareFunctionFields.FILL) ? Translate.value(
										errors.get(AnnotationSquareFunctionFields.FILL)!, {ns: "message"}
									) : ""}
									value={FunctionHelper.applyValue(
										annotationParameter.interiorColor, SquareAnnotation.getInteriorColorDefault()
									)}
									onInput={(e) => this.onChange(
										AnnotationSquareFunctionFields.FILL, e.currentTarget.value
									)}/>
								<ColorPicker inputId="control-fill" className="p-inputgroup-addon" format="hex"
											 defaultColor={SquareAnnotation.getInteriorColorDefault().replace(
												 "#", ""
											 )}
											 value={FunctionHelper.applyValue(
												 annotationParameter.interiorColor,
												 SquareAnnotation.getInteriorColorDefault()
											 ).replace("#", "")}
											 onChange={(e) => this.onChange(
												 AnnotationSquareFunctionFields.FILL, "#" + e.value
											 )}
											 appendTo={document.getElementById("root")}/>
							</div>
							{this.renderFieldError(errors.has(AnnotationSquareFunctionFields.FILL) ?
								Translate.value(
									errors.get(AnnotationSquareFunctionFields.FILL)!, {ns: "message"}
								) : ""
							)}
						</div>
					</div>
					<div className="p-col-12 p-grid"
						 hidden={
							 !this.state.fields?.contains(AnnotationSquareFunctionFields.BORDER) ||
							 annotationParameter.drawMode === DrawMode.Fill
						 }>
						<label className="p-col-12 p-sm-4" htmlFor="control-color">
							<Translate {...this.props} data="annotation.appearance.color.border" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8">
							<div className={"p-inputgroup" + (
								errors.has(AnnotationSquareFunctionFields.BORDER) ? " p-invalid" : ""
							)}>
								<InputText
									className={errors.has(AnnotationSquareFunctionFields.BORDER) ? "p-invalid" : ""}
									tooltip={errors.has(AnnotationSquareFunctionFields.BORDER) ? Translate.value(
										errors.get(AnnotationSquareFunctionFields.BORDER)!, {ns: "message"}
									) : ""}
									value={FunctionHelper.applyValue(
										annotationParameter.color, SquareAnnotation.getColorDefault()
									)}
									onInput={(e) => this.onChange(
										AnnotationSquareFunctionFields.BORDER, e.currentTarget.value
									)}/>
								<ColorPicker inputId="control-color" className="p-inputgroup-addon" format="hex"
											 defaultColor={SquareAnnotation.getColorDefault().replace(
												 "#", ""
											 )}
											 value={FunctionHelper.applyValue(
												 annotationParameter.color, SquareAnnotation.getColorDefault()
											 ).replace("#", "")}
											 onChange={(e) => this.onChange(
												 AnnotationSquareFunctionFields.BORDER, "#" + e.value
											 )}
											 appendTo={document.getElementById("root")}/>
							</div>
							{this.renderFieldError(errors.has(AnnotationSquareFunctionFields.BORDER) ?
								Translate.value(
									errors.get(AnnotationSquareFunctionFields.BORDER)!, {ns: "message"}
								) : ""
							)}
						</div>
					</div>
					<div className="p-col-12 p-grid"
						 hidden={
							 !this.state.fields?.contains(AnnotationSquareFunctionFields.STYLE) ||
							 annotationParameter.drawMode === DrawMode.Fill
						 }>
						<label className="p-col-12 p-sm-4" htmlFor="control-style">
							<Translate {...this.props} data="annotation.appearance.stroke.style.label"
									   namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8 p-grid">
							<Dropdown inputId="control-style"
									  value={FunctionHelper.applyValue(
										  annotationParameter.border?.borderStyle, Border.getBorderStyleDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "annotation.appearance.stroke.style.solid", {ns: "dialog"}
											  ),
											  value: BorderStyle.Solid
										  },
										  {
											  label: Translate.value(
												  "annotation.appearance.stroke.style.dashed",
												  {ns: "dialog"}
											  ),
											  value: BorderStyle.Dashed
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  AnnotationSquareFunctionFields.STYLE, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
					<div className="p-col-12 p-grid"
						 hidden={
							 !this.state.fields?.contains(AnnotationSquareFunctionFields.STROKE) ||
							 annotationParameter.drawMode === DrawMode.Fill
						 }>
						<label className="p-col-12 p-sm-4" htmlFor="control-stroke">
							<Translate {...this.props} data="annotation.appearance.stroke.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8">
							<InputNumber inputId="control-stroke" showButtons={true} suffix={" " + LengthUnit.PIXEL}
										 useGrouping={false}
										 value={FunctionHelper.applyValue(
											 annotationParameter.border?.borderWidth, Border.getBorderWidthDefault()
										 )}
										 min={Border.getBorderWidthMin()} step={1}
										 onChange={(e) => this.onChange(
											 AnnotationSquareFunctionFields.STROKE, e.value
										 )}/>
						</div>
					</div>
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(AnnotationSquareFunctionFields.OPACITY)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-opacity">
							<Translate {...this.props} data="annotation.appearance.opacity" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8">
							<InputNumber inputId="control-opacity" step={1} suffix=" %" showButtons={true}
										 useGrouping={false}
										 value={FunctionHelper.applyValue(
											 annotationParameter.opacity, SquareAnnotation.getOpacityDefault()
										 )}
										 min={SquareAnnotation.getOpacityMin()} max={SquareAnnotation.getOpacityMax()}
										 onChange={(e) => this.onChange(
											 AnnotationSquareFunctionFields.OPACITY, e.value
										 )}/>
						</div>
					</div>
				</fieldset>
			</>
		);
	}

	protected onChange(field: AnnotationSquareFunctionFields, value: any): void {
		let parameter: ToolboxAnnotationAnnotation = this.state.parameter!.data;
		let annotationParameter: SquareAnnotation = parameter.edit!.square!;
		let errors: Map<AnnotationSquareFunctionFields, string> = this.state.fieldErrors;

		switch (field) {
			case AnnotationSquareFunctionFields.CREATOR:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				annotationParameter.creator = value;
				break;
			case AnnotationSquareFunctionFields.SUBJECT:
				annotationParameter.subject = value;
				break;
			case AnnotationSquareFunctionFields.CONTENTS:
				annotationParameter.contents = value;
				break;
			case AnnotationSquareFunctionFields.INTENTS:
				annotationParameter.intents = value;
				break;
			case AnnotationSquareFunctionFields.BORDER:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (!validator.isHexColor(value)) {
					errors.set(field, "errors.fields.color");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				annotationParameter.color = value;
				break;
			case AnnotationSquareFunctionFields.FILL:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (!validator.isHexColor(value)) {
					errors.set(field, "errors.fields.color");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				annotationParameter.interiorColor = value;
				break;
			case AnnotationSquareFunctionFields.OPACITY:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = SquareAnnotation.getOpacityDefault();
				}

				annotationParameter.opacity = value;
				break;
			case AnnotationSquareFunctionFields.STROKE:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = Border.getBorderWidthDefault();
				}

				annotationParameter.border!.borderWidth = value;
				break;
			case AnnotationSquareFunctionFields.STYLE:
				annotationParameter.border!.borderStyle = value;
				break;
			case AnnotationSquareFunctionFields.DRAW_MODE:
				annotationParameter.drawMode = value;
				break;
		}

		this.setState({
			parameter: this.state.parameter
		});
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-edit"}/>
					<Translate {...this.props} data="annotation.edit" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	protected isEditPasswordNeeded(portalDocument: PortalDocument): boolean {
		return super.isEditPasswordNeeded(portalDocument) && !FunctionHelper.applyValue(
			portalDocument.metadata?.security?.canModifyAnnotations, Security.getCanModifyAnnotationsDefault()
		);
	}

	protected async submit(): Promise<void> {
		await super.submit();

		let toolboxOperation: BaseToolbox = BaseToolbox.fromJson({
			annotation: this.state.parameter!.data.toJson()
		});

		let operationData: Array<BaseToolbox> = [toolboxOperation];
		let webServiceType: WebServiceType = WebServiceTypes.TOOLBOX;
		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let selection: Array<PortalDocument> = documentManager.getSelection();

		for (let portalDocument of selection) {
			try {
				await this.props.context.session.executeRequest({
					webServiceType: webServiceType,
					document: portalDocument,
					operation: operationData,
					settings: Settings.fromJson({
						pdfa: {
							pdfaEditProfile: this.props.pdfaEditProfile
						},
						signature: {
							signatureEditProfile: this.props.signatureEditProfile
						},
						compress: {
							compressProfile: this.props.compressProfile,
							compressObjects: {
								objectStreamSize: this.props.objectStreamSize
							}
						}
					} as SettingsInterface)
				});
			} catch (exception: any) {
				if (await this.onError(exception)) {
					await this.submit();
				}
				return;
			}

			let message: Message = new Message("success.toolbox.annotation.edit.square", MessageType.SUCCESS);
			message.context = {filename: '"' + portalDocument.getFileName() + '" '};
			this.props.context.getPage().addMessage(message);
			this.hide();
		}
	}

	protected getDialogType(): PortalDialogType {
		return PortalDialogType.ANNOTATION_SQUARE_EDIT;
	}

	protected getDefaultParameter(): Parameter<ToolboxAnnotationAnnotation> {
		return DialogDefault.getParameter(
			this.getDialogType(), this.props.context.documentManager.getSelection()
		);
	}
}

const AnnotationSquareEditDialog = React.memo(AnnotationSquareEditDialogDefinition,
	(
		prevProps: Readonly<PortalDialogProperties<ToolboxAnnotationAnnotation>>,
		nextProps: Readonly<PortalDialogProperties<ToolboxAnnotationAnnotation>>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default AnnotationSquareEditDialog;